import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
export interface ToastMessage {
  /** Unique id, auto-generated — used internally to remove the toast after its life expires */
  id: number;
  severity:string ;
  summary?: string;
  detail?: string;
  /** Duration in ms before auto-dismiss. Defaults to 3000 if not provided. */
  life?: number;
}

/**
 * Drop-in replacement for PrimeNG's MessageService.
 */
@Injectable({ providedIn: 'root' })
export class AppMessageService {
  private readonly messagesSubject = new BehaviorSubject<ToastMessage[]>([]);
  /** Subscribed to by AppToastComponent to render active toasts */
  messages$ = this.messagesSubject.asObservable();

  private nextId = 0;
  private readonly DEFAULT_LIFE = 3000;

  /** Matches PrimeNG's `add(message: Message)` signature */
  add(message: Omit<ToastMessage, 'id'>): void {
    const toast: ToastMessage = {
      id: this.nextId++,
      life: message.life ?? this.DEFAULT_LIFE,
      ...message,
    };

    const current = this.messagesSubject.value;
    this.messagesSubject.next([...current, toast]);

    setTimeout(() => this.removeMessage(toast.id), toast.life);
  }

  /** Matches PrimeNG's `addAll(messages: Message[])` signature */
  addAll(messages: Omit<ToastMessage, 'id'>[]): void {
    messages.forEach((m) => this.add(m));
  }

  /** Matches PrimeNG's `clear()` signature — removes all active toasts */
  clear(): void {
    this.messagesSubject.next([]);
  }

  /** Called by AppToastComponent's close button to dismiss a single toast early */
  removeMessage(id: number): void {
    const current = this.messagesSubject.value;
    this.messagesSubject.next(current.filter((t) => t.id !== id));
  }
}